// src/pages/DemoPage.js
import React, { useState, useRef } from 'react';
import { Box, Typography, Button, IconButton, Modal, Fade, Backdrop } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { assetUrl } from '../utils/assets';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const DEMO_VIDEOS = [
  {
    id: 'hindi',
    title: 'Hindi',
    language: 'हिंदी',
    flag: '🇮🇳',
    speakers: '600M+ speakers',
    description: 'AI-dubbed with authentic Hindi pronunciation',
    videoUrl: assetUrl('videoHindi.mp4', '/demo/hindi.mp4'),
    features: ['Authentic Accent', 'Regional Variations', 'Emotional Expression'],
  },
  {
    id: 'marathi',
    title: 'Marathi',
    language: 'मराठी',
    flag: '🇮🇳',
    speakers: '83M+ speakers',
    description: 'AI-dubbed with authentic Marathi pronunciation',
    videoUrl: assetUrl('marathi.mov', '/demo/marathi.mov'),
    features: ['Native Pronunciation', 'Regional Nuance', 'Voice Preservation'],
  },
  {
    id: 'kannada',
    title: 'Kannada',
    language: 'ಕನ್ನಡ',
    flag: '🇮🇳',
    speakers: '45M+ speakers',
    description: 'AI-dubbed with authentic Kannada intonation',
    videoUrl: assetUrl('kannadaDemo.mp4', '/demo/kannada.mp4'),
    features: ['Native Pronunciation', 'Regional Nuance', 'Voice Preservation'],
  },
  {
    id: 'original',
    title: 'English',
    language: 'English',
    flag: '🇺🇸',
    speakers: '1.5B speakers',
    description: 'Reference clip — clean English delivery',
    videoUrl: assetUrl('original.mp4', '/demo/original.mp4'),
    features: ['Natural Delivery', 'Clear Audio', 'Professional Content'],
  },
  {
    id: 'spanish',
    title: 'Spanish',
    language: 'Español',
    flag: '🇪🇸',
    speakers: '500M+ speakers',
    description: 'AI-dubbed with perfect Spanish lip-sync',
    videoUrl: assetUrl('spanish.mp4', '/demo/spanish.mp4'),
    features: ['Perfect Lip-Sync', 'Voice Preservation', 'Natural Pronunciation'],
  },
  {
    id: 'french',
    title: 'French',
    language: 'Français',
    flag: '🇫🇷',
    speakers: '280M+ speakers',
    description: 'AI-dubbed with authentic French accent',
    videoUrl: assetUrl('french.mp4', '/demo/french.mp4'),
    features: ['Authentic Accent', 'Cultural Nuances', 'Emotional Tone'],
  },
  {
    id: 'german',
    title: 'German',
    language: 'Deutsch',
    flag: '🇩🇪',
    speakers: '100M+ speakers',
    description: 'AI-dubbed with precise German pronunciation',
    videoUrl: assetUrl('german.mp4', '/demo/german.mp4'),
    features: ['Precise Pronunciation', 'Technical Accuracy', 'Professional Quality'],
  },
  {
    id: 'mandarin',
    title: 'Mandarin',
    language: '中文',
    flag: '🇨🇳',
    speakers: '900M+ speakers',
    description: 'AI-dubbed with natural Mandarin intonation',
    videoUrl: '/demo/mandarin.mp4',
    features: ['Natural Intonation', 'Tonal Accuracy', 'Cultural Context'],
  },
];

const BENEFITS = [
  { title: 'Minutes, Not Weeks', body: 'Complete dubbing in 2–5 minutes vs. weeks of traditional studio production.' },
  { title: 'Voice Preservation', body: 'Maintains original voice characteristics, tone, and emotion across all languages.' },
  { title: 'Perfect Lip-Sync', body: 'AI-powered lip synchronisation that looks completely natural on screen.' },
  { title: 'Global Reach', body: 'Instantly reach 3+ billion people with fully localised, multilingual content.' },
];

export default function DemoPage() {
  const navigate = useNavigate();
  const [modalVideo, setModalVideo] = useState(null);

  useSEO({
    title: 'DubLabs Demo — Watch AI Video Dubbing in 8 Languages',
    description: 'See DubLabs in action across 8 languages — perfect lip-sync, voice preservation, and native-sounding output in Hindi, Marathi, Kannada and more.',
    canonical: 'https://dublabs.ai/demo',
  });

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh' }}>

      {/* Header */}
      <Box sx={{ py: { xs: 8, md: 14 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: '1100px', mx: 'auto' }}>
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Live Demo
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.4rem' }, fontWeight: 800, letterSpacing: '-0.035em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Eight languages. Real results.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1.1rem', fontFamily: 'Inter, sans-serif', maxWidth: '520px', mx: 'auto', lineHeight: 1.65, mb: 5 }}>
              A hand-picked set of DubLabs outputs across eight languages — perfect lip-sync, voice preservation, and native-sounding delivery in every clip.
            </Typography>

            {/* Special demo links */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/mbs')}
                sx={{
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  px: 3,
                  py: 1.25,
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.22)', color: '#fff' },
                }}
              >
                Saudi MBS Demo
              </Button>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/uae')}
                sx={{
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  px: 3,
                  py: 1.25,
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.22)', color: '#fff' },
                }}
              >
                UAE Sheikh Demo
              </Button>
            </Box>
          </Box>

          {/* Video grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2,
              mb: { xs: 4, md: 6 },
            }}
          >
            {DEMO_VIDEOS.map((video, i) => (
              <Box
                key={video.id}
                component={motion.div}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                custom={i} variants={fadeUp}
                onClick={() => setModalVideo(video)}
                sx={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  backgroundColor: '#0A0A0A',
                  border: '1px solid rgba(255,255,255,0.06)',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.28)',
                    transform: 'translateY(-2px)',
                  },
                  '&:hover .thumb-overlay': {
                    backgroundColor: 'rgba(0,0,0,0.25)',
                  },
                }}
              >
                {/* Video thumbnail */}
                <Box sx={{ position: 'relative', aspectRatio: '16/9', backgroundColor: '#111', overflow: 'hidden' }}>
                  <video
                    src={video.videoUrl}
                    preload="metadata"
                    muted
                    playsInline
                    style={{
                      position: 'absolute', top: 0, left: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                    }}
                  />
                  <Box
                    className="thumb-overlay"
                    sx={{
                      position: 'absolute', inset: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.42)',
                      transition: 'background-color 0.25s ease',
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.75rem', mb: 1 }}>{video.flag}</Typography>
                      <Box sx={{ width: 46, height: 46, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>
                        <PlayArrowIcon sx={{ color: '#fff', fontSize: 24, ml: 0.5 }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Card info */}
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '15px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em' }}>
                      {video.language}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', fontFamily: 'Inter, sans-serif' }}>
                      {video.speakers}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontFamily: 'Inter, sans-serif', mb: 2 }}>
                    {video.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {video.features.map((f) => (
                      <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 13, color: '#22C55E' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '12.5px', fontFamily: 'Inter, sans-serif' }}>{f}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Disclaimer */}
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ textAlign: 'center', mb: { xs: 10, md: 16 } }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
              Demo purposes only. All video content belongs to respective copyright holders. DubLabs does not claim ownership of the original content shown.
            </Typography>
          </Box>

          {/* Benefits */}
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{ mb: { xs: 10, md: 16 } }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2, textAlign: 'center' }}>
              Why DubLabs
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 8, textAlign: 'center' }}>
              Why enterprises choose DubLabs
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              {BENEFITS.map((b, i) => (
                <Box
                  key={b.title}
                  component={motion.div}
                  initial="hidden" whileInView="visible" viewport={{ once: true }}
                  custom={i} variants={fadeUp}
                  sx={{
                    p: 3.5,
                    borderRadius: '12px',
                    backgroundColor: '#0A0A0A',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '15px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em', mb: 1 }}>
                    {b.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.65 }}>
                    {b.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Bottom CTA */}
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            sx={{
              textAlign: 'center', p: { xs: 5, md: 8 }, borderRadius: '20px',
              backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.07)',
              position: 'relative', overflow: 'hidden',
              '&::before': { content: '""', position: 'absolute', top: 0, left: '25%', right: '25%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)' },
            }}
          >
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.03em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Ready to transform your content?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', fontFamily: 'Inter, sans-serif', mb: 5, maxWidth: '400px', mx: 'auto', lineHeight: 1.65 }}>
              Contact our team for a personalised demo and quote tailored to your content volume.
            </Typography>
            <Button
              component={motion.button}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/contact')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{ backgroundColor: '#FFFFFF', color: '#000000', fontWeight: 700, fontSize: '15px', px: 4, py: 1.5, borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', '&:hover': { backgroundColor: '#F0F0F0' } }}
            >
              Get in Touch
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Video Modal */}
      <Modal
        open={!!modalVideo}
        onClose={() => setModalVideo(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 400, sx: { backgroundColor: 'rgba(0,0,0,0.88)' } } }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
      >
        <Fade in={!!modalVideo}>
          <Box sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '900px',
            backgroundColor: '#0A0A0A',
            borderRadius: '16px',
            p: { xs: 2.5, md: 3.5 },
            outline: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <IconButton
              onClick={() => setModalVideo(null)}
              sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.06)', zIndex: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {modalVideo && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                  <Typography sx={{ fontSize: '1.6rem' }}>{modalVideo.flag}</Typography>
                  <Box>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '16px', fontFamily: 'Inter, sans-serif' }}>
                      {modalVideo.language}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                      {modalVideo.description} · {modalVideo.speakers}
                    </Typography>
                  </Box>
                </Box>

                <video
                  controls
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                  style={{ width: '100%', height: 'auto', maxHeight: '65vh', borderRadius: '10px', backgroundColor: '#000', display: 'block' }}
                >
                  <source
                    src={modalVideo.videoUrl}
                    type={modalVideo.videoUrl.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'}
                  />
                </video>

                <Box sx={{ mt: 2.5, display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                  {modalVideo.features.map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CheckCircleIcon sx={{ fontSize: 13, color: '#22C55E' }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>{f}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
